/** Date helpers. German formatting throughout; ISO for storage. */

import type { IsoDate, IsoInstant } from './types';

export function todayIso(): IsoDate {
  return new Date().toISOString().slice(0, 10);
}

export function nowIso(): IsoInstant {
  return new Date().toISOString();
}

export function toIsoDate(date: Date): IsoDate {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** `2026-07-14` becomes `14.07.2026`. */
export function formatDate(iso: IsoDate | undefined): string {
  if (!iso) return '';
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}.${month}.${year}`;
}

/** `2026-07-14T09:31:00Z` becomes `14.07.2026, 11:31` in local time. */
export function formatDateTime(iso: IsoInstant | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatTime(iso: IsoInstant | undefined): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

/** Combine a local date and `HH:mm` into a UTC instant. */
export function combineDateTime(date: IsoDate, time: string): IsoInstant {
  const [hours, minutes] = time.split(':').map(Number);
  const local = new Date(`${date}T00:00:00`);
  local.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return local.toISOString();
}

/** `HH:mm` in local time, for populating a time input. */
export function timeInputValue(iso: IsoInstant | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(fromIso.slice(0, 10) + 'T00:00:00Z').getTime();
  const to = new Date(toIso.slice(0, 10) + 'T00:00:00Z').getTime();
  return Math.round((to - from) / 86_400_000);
}

export function daysSince(iso: IsoInstant | undefined): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

export function startOfMonth(iso: IsoDate): IsoDate {
  return `${iso.slice(0, 7)}-01`;
}

export function endOfMonth(iso: IsoDate): IsoDate {
  const date = new Date(`${iso.slice(0, 7)}-01T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + 1);
  date.setUTCDate(0);
  return date.toISOString().slice(0, 10);
}

export function startOfYear(year: number): IsoDate {
  return `${year}-01-01`;
}

export function endOfYear(year: number): IsoDate {
  return `${year}-12-31`;
}

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
] as const;

export function monthName(monthIndexZeroBased: number): string {
  return MONTH_NAMES[monthIndexZeroBased] ?? '';
}

/** `2026-07` becomes `Juli 2026`. */
export function formatMonthBucket(bucket: string): string {
  const [year, month] = bucket.split('-');
  return `${monthName(Number(month) - 1)} ${year}`;
}

/** Human label for an arbitrary range, collapsing whole months and years. */
export function describePeriod(fromIso: IsoDate, toIso: IsoDate): string {
  if (fromIso === startOfYear(Number(fromIso.slice(0, 4))) && toIso === endOfYear(Number(fromIso.slice(0, 4)))) {
    return `Jahr ${fromIso.slice(0, 4)}`;
  }
  if (fromIso === startOfMonth(fromIso) && toIso === endOfMonth(fromIso) && fromIso.slice(0, 7) === toIso.slice(0, 7)) {
    return formatMonthBucket(fromIso.slice(0, 7));
  }
  return `${formatDate(fromIso)} – ${formatDate(toIso)}`;
}

/** Minutes between two instants, for shift duration. */
export function minutesBetween(fromIso: IsoInstant, toIso: IsoInstant): number {
  return Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60_000);
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours}:${String(rest).padStart(2, '0')} h`;
}
